import { useEffect, useState } from 'react'

import { useInView } from 'react-intersection-observer'

import { IconLoading } from '@/assets/icons/icon-loading'
import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { SearchInput } from '@/components/ui/search-input'
import { useGetCustomListFans } from '@/modules/creator-settings/lists/custom-lists/hooks/useGetCustomListFans.ts'
import { NoSpendersFanItem } from '@/modules/creator-settings/lists/no-spenders/components/no-spenders-fan-item'
import { noop } from '@/utils/misc.ts'

import type { DialogProps } from '../types'

interface Props extends DialogProps {
  listId: number
}

export const CustomListsViewFanDialog = ({
  isOpen,
  onClose,
  listId,
}: Props) => {
  const { ref, inView } = useInView()
  const [search, setSearch] = useState('')
  const { fans, isLoading, hasNextPage, fetchNextPage, isFetching } =
    useGetCustomListFans(listId, search)

  useEffect(() => {
    if (inView && hasNextPage && !isFetching) {
      fetchNextPage()
    }
  }, [fetchNextPage, inView, hasNextPage, isFetching])

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
    >
      <Dialog.Header>
        <Dialog.Title>View Fans</Dialog.Title>
      </Dialog.Header>
      <Dialog.Description>
        <div className="flex flex-col gap-4 pt-6">
          <SearchInput
            className="w-full"
            placeholder="Search"
            value={search}
            onInputChange={setSearch}
          />
          <div className="flex max-h-[50vh] min-h-[50vh] flex-col gap-2 overflow-y-auto pr-2">
            {isLoading ? (
              Array.from({ length: 20 }).map((_, index) => (
                <NoSpendersFanItem
                  isLoading={true}
                  key={index}
                />
              ))
            ) : fans?.length === 0 ? (
              <div className="flex items-center justify-center">
                <span className="text-sm text-gray-500">No fans found</span>
              </div>
            ) : (
              <>
                {fans?.map((fan) => (
                  <NoSpendersFanItem
                    isLoading={false}
                    key={fan.id}
                    fan={fan}
                    onView={noop}
                  />
                ))}
                {hasNextPage && (
                  <div
                    ref={ref}
                    className="flex items-center justify-center"
                  >
                    <IconLoading className="h-4 w-4 animate-spin" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Dialog.Description>
      <Dialog.Footer>
        <Button
          variant="outline"
          className="w-full"
          onClick={onClose}
        >
          Back
        </Button>
      </Dialog.Footer>
    </Dialog>
  )
}
